import { FC, useState, useCallback, useContext, useEffect } from 'react'
import * as React from 'react'
import { InputBase, Collapse } from '@material-ui/core'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'
import { ActivityFilterItemById } from 'generated/graphql'
import useJobCategoryStore from '@app.feature/jobCategory/store/store.jobCategory'
import { Button } from 'components/custom-material-ui'
import ExpandMoreIcon from 'svgIcons/ExpandMore'
import MagnifyingGlassIcon from 'svgIcons/MagnifyingGlass'
import { RecruitListScreenContext, FilterByKeys } from '../../context'
import useCategoryIdsSyncEffect from '../../hook/useCategoryIdsSyncEffect'
import RecruitFilterItem from './RecruitFilterItem.pc'

export interface FilterListItem<T = ActivityFilterItemById> {
  filterTitle: string
  filterItems: Array<T>
  filterKey: FilterByKeys
  DetailPanel?: FC<unknown>
  itemDetailPanelAction?: (item: T) => void
}

interface RecruitFilterPCProps {
  filterList: FilterListItem[]
}

const RecruitFilterPC: FC<RecruitFilterPCProps> = ({ filterList }) => {
  const [expanded, setExpanded] = useState(false)
  const [openDetailPanelKey, setOpenDetailPanelKey] =
    useState<FilterByKeys | null>(null)
  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded)
  }, [expanded])
  const showExpanded = useCallback(() => {
    setExpanded(true)
  }, [])

  const recruitContext = useContext(RecruitListScreenContext)
  const { state, dispatch } = recruitContext

  const [keywordState, setKeywordState] = useState(state?.filterBy?.q || '')

  const showSelectedIds = useJobCategoryStore((state) => state.showSelectedIds)
  const idsByDepth = useJobCategoryStore((state) => state.idsByDepth)
  const getCategoriesByIds = useJobCategoryStore(
    (state) => state.getCategoriesByIds,
  )

  useEffect(() => {
    setKeywordState(state?.filterBy?.q || '')
  }, [state?.filterBy?.q])

  useCategoryIdsSyncEffect({
    getCategoriesByIds,
    idsByDepth,
    showSelectedIds,
    recruitContext,
  })

  const [debounce] = useDebouncedCallback((keyword) => {
    dispatch({
      type: 'FILTER_BY_SET_KEYWORD',
      fieldName: 'q',
      listItem: keyword,
    })
  }, 800)

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debounce(e.target.value)
      setKeywordState(e.target.value)
    },
    [],
  )

  return (
    <StyledWrapper>
      {filterList.map(
        ({
          filterTitle,
          filterItems,
          filterKey,
          DetailPanel,
          itemDetailPanelAction,
        }) => (
          <RecruitFilterItem
            key={filterKey}
            width={200}
            filterTitle={filterTitle}
            filterItems={filterItems}
            filterKey={filterKey}
            isHidden={!expanded}
            isDetailPanelOpen={filterKey === openDetailPanelKey}
            setOpenDetailPanelKey={setOpenDetailPanelKey}
            DetailPanel={DetailPanel}
            onTitleClick={showExpanded}
            onClickForDetailPanel={itemDetailPanelAction}
          />
        ),
      )}
      <div className="filter">
        <InputBase
          margin="none"
          placeholder="공고명, 기업, 직무 검색"
          inputProps={{
            style: { padding: 0 },
            'data-testid': 'new-activity-list-filter-search-input',
          }}
          onChange={handleInputChange}
          value={keywordState}
          startAdornment={<MagnifyingGlassIcon className="icon-search" />}
          id="organizationName"
          onFocus={showExpanded}
          className="organization-name"
        />
        <Collapse in={expanded}>
          <div className="filter-collapse" />
        </Collapse>
      </div>
      <div className="filter-button-toggle">
        <Button onClick={toggleExpanded} className="button-toggle">
          <ExpandMoreIcon
            className={clsx('icon-expand-more', {
              expanded,
            })}
          />
        </Button>
      </div>
    </StyledWrapper>
  )
}

export default RecruitFilterPC

const StyledWrapper = styled.section`
  ${({ theme }) => css`
    display: flex;
    flex-grow: 1;
    position: relative;

    .filter {
      width: 100%;
      text-align: left;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 15px;
      padding: 0;
      border: 1px solid ${theme.line.line_2};
      border-left: 1px solid transparent;

      &:first-child {
        border-left: 1px solid ${theme.line.line_2};
      }

      .icon-search {
        margin-right: 5px;
        font-size: 16px;
      }

      .organization-name {
        display: flex;
        align-items: center;
        padding: 15px 15px;

        & input[type='text']::-ms-clear {
          width: 0;
          height: 0;
          display: none;
        }
      }

      .filter-collapse {
        height: 254px;
        background-color: ${theme.background.bg_3};
        border-top: 1px solid ${theme.line.line_2}};
      }
    }

    .filter-button-toggle {
      width: 100%;
      max-width: 51px;
      max-height: 51px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 15px;
      padding: 0;
      border: 1px solid #102f53;

      .button-toggle {
        width: 100%;
        height: 100%;
        border-radius: 0;
        background-color: #102f53;

        &:hover {
          background-color: #102f53e6;
        }

        .icon-expand-more {
          fill: white;
          width:21px;
          height:21px;
          transform: rotate(0deg);
          transition: transform 0.15s ease-out;

          &.expanded {
            transform: rotate(180deg);
          }
        }
      }
    }
  `}
`
